/**
 * iter215 — Driver PWA
 *
 * Mobile-first single page at /driver (login/cookie) or /driver/t/:token
 * (signed link, no login). Shows today's ordered run with per-stop
 * Arrive/Depart/POD flow. Signature captured via HTML5 canvas, photos
 * via camera-capable file input, GPS via navigator.geolocation.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Truck, MapPin, Signature, Camera, CheckCircle, Warning, X, ArrowRight,
} from '@phosphor-icons/react';
import {
  getDriverRun, driverArrive, driverDepart, driverPod, driverPing,
  driverArriveStop, driverDepartStop, driverPodStop,
} from '../lib/api';

const STATE_COLOR = {
  planned: 'bg-blue-100 text-blue-800 border-blue-300',
  in_transit: 'bg-amber-100 text-amber-800 border-amber-300',
  delivered: 'bg-emerald-100 text-emerald-800 border-emerald-300',
};

function useDriverToken() {
  const { token: pathToken } = useParams();
  const [search] = useSearchParams();
  return pathToken || search.get('token') || null;
}

async function grabGps() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lon: p.coords.longitude, accuracy_m: p.coords.accuracy }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 15000 },
    );
  });
}

function SignaturePad({ onDone, onCancel }) {
  const ref = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, []);

  const pos = (e) => {
    const c = ref.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return {
      x: ((t.clientX - rect.left) / rect.width) * c.width,
      y: ((t.clientY - rect.top) / rect.height) * c.height,
    };
  };
  const start = (e) => {
    e.preventDefault();
    const c = ref.current;
    const ctx = c.getContext('2d');
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    setDrawing(true);
    setDirty(true);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const c = ref.current;
    const ctx = c.getContext('2d');
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };
  const end = () => setDrawing(false);

  const clear = () => {
    const c = ref.current;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, c.width, c.height);
    setDirty(false);
  };
  const finish = () => {
    if (!dirty) return toast.error('Signature is empty');
    onDone(ref.current.toDataURL('image/png'));
  };

  return (
    <div className="border border-slate-300 rounded p-2 bg-white">
      <canvas
        ref={ref} width={600} height={200}
        className="w-full h-40 border border-slate-200 rounded touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="driver-signature-canvas"
      />
      <div className="flex gap-2 mt-2">
        <button type="button" onClick={clear} className="text-xs px-3 py-2 border border-slate-300 rounded" data-testid="driver-signature-clear">Clear</button>
        <button type="button" onClick={onCancel} className="text-xs px-3 py-2 border border-slate-300 rounded">Cancel</button>
        <button type="button" onClick={finish} className="text-xs px-3 py-2 bg-emerald-600 text-white rounded ml-auto" data-testid="driver-signature-save">Save signature</button>
      </div>
    </div>
  );
}

function PodPanel({ job, token, onSaved, submitFn, defaultQty }) {
  const [recipient, setRecipient] = useState('');
  const [signature, setSignature] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [showPad, setShowPad] = useState(false);
  const [notes, setNotes] = useState('');
  const [delivered, setDelivered] = useState(defaultQty ?? job.pallets ?? job.cartons ?? 1);
  const [shortQty, setShortQty] = useState(0);
  const [damaged, setDamaged] = useState(0);
  const [busy, setBusy] = useState(false);

  const addPhoto = (e) => {
    const files = Array.from(e.target.files || []);
    if (photos.length + files.length > 5) {
      toast.error('Max 5 photos');
      return;
    }
    files.forEach((f) => {
      const reader = new FileReader();
      reader.onload = () => setPhotos((prev) => (prev.length >= 5 ? prev : [...prev, reader.result]));
      reader.readAsDataURL(f);
    });
    e.target.value = '';
  };

  const submit = async () => {
    if (!recipient) return toast.error('Recipient name required');
    setBusy(true);
    try {
      const gps = await grabGps();
      const payload = {
        token, gps,
        recipient_name: recipient,
        signature_data_url: signature,
        photos_data_urls: photos,
        delivered_qty: Number(delivered) || 0,
        short_qty: Number(shortQty) || 0,
        damaged_qty: Number(damaged) || 0,
        notes: notes || null,
      };
      if (submitFn) {
        await submitFn(payload);
      } else {
        await driverPod(job.id, payload);
      }
      toast.success('POD submitted');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'POD failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 border-t border-slate-200 pt-3" data-testid={`driver-pod-panel-${job.id}`}>
      <div className="text-sm font-semibold">POD capture</div>
      <div>
        <label className="text-xs text-slate-600">Recipient name *</label>
        <input value={recipient} onChange={(e) => setRecipient(e.target.value)}
               className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
               placeholder="Person receiving the delivery"
               data-testid="driver-pod-recipient" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-xs text-slate-600">Delivered</label>
          <input type="number" min="0" value={delivered} onChange={(e) => setDelivered(e.target.value)}
                 className="w-full border border-slate-300 rounded px-2 py-2 text-sm"
                 data-testid="driver-pod-delivered" />
        </div>
        <div>
          <label className="text-xs text-slate-600">Short</label>
          <input type="number" min="0" value={shortQty} onChange={(e) => setShortQty(e.target.value)}
                 className="w-full border border-slate-300 rounded px-2 py-2 text-sm"
                 data-testid="driver-pod-short" />
        </div>
        <div>
          <label className="text-xs text-slate-600">Damaged</label>
          <input type="number" min="0" value={damaged} onChange={(e) => setDamaged(e.target.value)}
                 className="w-full border border-slate-300 rounded px-2 py-2 text-sm"
                 data-testid="driver-pod-damaged" />
        </div>
      </div>
      <div>
        <label className="text-xs text-slate-600">Notes</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                  placeholder="e.g. Left at reception with security" />
      </div>

      {/* Signature */}
      {showPad ? (
        <SignaturePad onDone={(url) => { setSignature(url); setShowPad(false); toast.success('Signature captured'); }}
                      onCancel={() => setShowPad(false)} />
      ) : (
        <button type="button" onClick={() => setShowPad(true)}
                className={`w-full py-3 rounded text-sm flex items-center justify-center gap-2 border ${signature ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-slate-300'}`}
                data-testid="driver-pod-open-signature">
          <Signature size={16} weight="bold" />
          {signature ? 'Signature captured · tap to replace' : 'Capture recipient signature'}
        </button>
      )}

      {/* Photos */}
      <div>
        <label className="w-full py-3 rounded text-sm flex items-center justify-center gap-2 border border-slate-300 cursor-pointer" data-testid="driver-pod-add-photo">
          <Camera size={16} weight="bold" />
          Take/attach photo ({photos.length}/5)
          <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={addPhoto} />
        </label>
        {photos.length > 0 && (
          <div className="grid grid-cols-5 gap-1 mt-2">
            {photos.map((p, i) => (
              <div key={i} className="relative">
                <img src={p} alt="" className="w-full aspect-square object-cover rounded border border-slate-200" />
                <button type="button" onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))}
                        className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-600 text-white text-[10px] flex items-center justify-center">×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <button type="button" onClick={submit} disabled={busy}
              className="w-full py-3 rounded bg-emerald-600 text-white text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="driver-pod-submit">
        <CheckCircle size={16} weight="bold" />
        {busy ? 'Submitting…' : 'Submit POD & mark delivered'}
      </button>
    </div>
  );
}

function StopCard({ job, token, seq, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [showPod, setShowPod] = useState(false);
  const done = job.status === 'delivered';
  const arrived = !!job.arrived_at;
  const departed = !!job.departed_at;

  const arrive = async () => {
    try {
      const gps = await grabGps();
      await driverArrive(job.id, { token, gps });
      toast.success('Arrival recorded');
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Arrive failed'); }
  };
  const depart = async () => {
    try {
      const gps = await grabGps();
      await driverDepart(job.id, { token, gps });
      toast.success('Departure recorded');
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Depart failed'); }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`driver-stop-${job.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-white shrink-0 ${done ? 'bg-emerald-600' : arrived ? 'bg-amber-500' : 'bg-blue-600'}`}>
          {done ? <CheckCircle size={18} weight="fill" /> : seq}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-mono text-slate-500">{job.job_number}</div>
          <div className="font-semibold text-sm truncate">{job.customer_name || '—'}</div>
          <div className="text-xs text-slate-600 flex items-start gap-1">
            <MapPin size={12} className="mt-0.5 shrink-0" />
            <span className="line-clamp-2">{job.delivery_address || job.delivery_location || '—'}</span>
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {job.pallets}p · {job.weight_kg}kg
            {job.arrival_eta && <> · ETA {new Date(job.arrival_eta).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</>}
            {job.dangerous_goods && <> · ⚠️ DG</>}
            {job.temperature && job.temperature !== 'ambient' && <> · ❄️ {job.temperature}</>}
          </div>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded border ${STATE_COLOR[job.status] || 'bg-slate-100 border-slate-300'}`}>
          {job.status}
        </span>
      </div>

      {job.special_instructions && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-900">
          📋 {job.special_instructions}
        </div>
      )}

      {!done && (
        <div className="grid grid-cols-3 gap-2">
          <button type="button" onClick={arrive} disabled={arrived}
                  className={`py-2 rounded text-xs font-semibold ${arrived ? 'bg-slate-100 text-slate-500' : 'bg-blue-600 text-white'}`}
                  data-testid={`driver-arrive-${job.id}`}>
            {arrived ? '✓ Arrived' : 'Arrive'}
          </button>
          <button type="button" onClick={() => setShowPod((v) => !v)} disabled={!arrived}
                  className={`py-2 rounded text-xs font-semibold ${!arrived ? 'bg-slate-100 text-slate-400' : 'bg-emerald-600 text-white'}`}
                  data-testid={`driver-pod-toggle-${job.id}`}>
            POD
          </button>
          <button type="button" onClick={depart} disabled={!arrived || !done && !departed && job.status !== 'delivered'}
                  className={`py-2 rounded text-xs font-semibold ${departed ? 'bg-slate-100 text-slate-500' : (!arrived ? 'bg-slate-100 text-slate-400' : 'bg-slate-600 text-white')}`}
                  data-testid={`driver-depart-${job.id}`}>
            {departed ? '✓ Departed' : 'Depart'}
          </button>
        </div>
      )}

      {showPod && !done && <PodPanel job={job} token={token} onSaved={() => { setShowPod(false); onChanged?.(); }} />}

      {done && (
        <div className="text-xs text-emerald-700 bg-emerald-50 rounded p-2 border border-emerald-200" data-testid={`driver-pod-summary-${job.id}`}>
          Delivered to <span className="font-semibold">{job.pod_recipient_name || '—'}</span>
          {job.pod_captured_at && <> at {new Date(job.pod_captured_at).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</>}
          {job.pod_short_qty > 0 && <> · <span className="text-amber-700">{job.pod_short_qty} short</span></>}
          {job.pod_damaged_qty > 0 && <> · <span className="text-red-700">{job.pod_damaged_qty} damaged</span></>}
          {job.pod_photo_paths?.length > 0 && <> · {job.pod_photo_paths.length} photo(s)</>}
        </div>
      )}

      <button type="button" onClick={() => setExpanded((v) => !v)}
              className="text-[10px] text-slate-500 flex items-center gap-1">
        {expanded ? '– Hide history' : '+ Show history'}
      </button>
      {expanded && job.history && (
        <div className="text-[10px] text-slate-600 space-y-0.5">
          {job.history.slice(-6).map((h, i) => (
            <div key={i}>· {new Date(h.at).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })} — {h.state}{h.note ? ` · ${h.note}` : ''}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// iter232 — Multi-stop milk run: parent card with N numbered sub-stop cards.
function MultiStopSubCard({ job, stop, token, parentSeq, onChanged }) {
  const [showPod, setShowPod] = useState(false);
  const arrived = !!stop.arrived_at;
  const departed = !!stop.departed_at;
  const podDone = !!stop.pod_captured_at;
  const complete = stop.status === 'completed' || (podDone && departed);

  const arrive = async () => {
    try {
      const gps = await grabGps();
      await driverArriveStop(job.id, stop.sequence, { token, gps });
      toast.success(`Arrived at stop ${stop.sequence}`);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Arrive failed'); }
  };
  const depart = async () => {
    try {
      const gps = await grabGps();
      await driverDepartStop(job.id, stop.sequence, { token, gps });
      toast.success(`Departed stop ${stop.sequence}`);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Depart failed'); }
  };

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 space-y-2" data-testid={`driver-substop-${job.id}-${stop.sequence}`}>
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white shrink-0 text-sm
          ${complete ? 'bg-emerald-600' : podDone ? 'bg-emerald-500' : arrived ? 'bg-amber-500' : 'bg-blue-600'}`}>
          {complete ? <CheckCircle size={16} weight="fill" /> : stop.sequence}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
            Stop {stop.sequence} of {parentSeq}
            {stop.status && <> · {stop.status}</>}
          </div>
          <div className="font-semibold text-sm truncate">{stop.customer_name || '—'}</div>
          <div className="text-xs text-slate-600 flex items-start gap-1">
            <MapPin size={12} className="mt-0.5 shrink-0" />
            <span className="line-clamp-2">{stop.address || '—'}</span>
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {(stop.pallets ?? 0)}p · {(stop.weight_kg ?? 0)}kg
            {stop.window_end && <> · by {stop.window_end}</>}
          </div>
        </div>
      </div>
      {stop.special_instructions && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-900">
          📋 {stop.special_instructions}
        </div>
      )}
      {!complete && (
        <div className="grid grid-cols-3 gap-2">
          <button type="button" onClick={arrive} disabled={arrived}
                  className={`py-2 rounded text-xs font-semibold ${arrived ? 'bg-slate-100 text-slate-500' : 'bg-blue-600 text-white'}`}
                  data-testid={`driver-substop-arrive-${job.id}-${stop.sequence}`}>
            {arrived ? '✓ Arrived' : 'Arrive'}
          </button>
          <button type="button" onClick={() => setShowPod((v) => !v)} disabled={!arrived || podDone}
                  className={`py-2 rounded text-xs font-semibold ${(!arrived || podDone) ? 'bg-slate-100 text-slate-400' : 'bg-emerald-600 text-white'}`}
                  data-testid={`driver-substop-pod-${job.id}-${stop.sequence}`}>
            {podDone ? '✓ POD' : 'POD'}
          </button>
          <button type="button" onClick={depart} disabled={!arrived || departed}
                  className={`py-2 rounded text-xs font-semibold ${departed ? 'bg-slate-100 text-slate-500' : (!arrived ? 'bg-slate-100 text-slate-400' : 'bg-slate-600 text-white')}`}
                  data-testid={`driver-substop-depart-${job.id}-${stop.sequence}`}>
            {departed ? '✓ Departed' : 'Depart'}
          </button>
        </div>
      )}
      {showPod && !podDone && (
        <PodPanel
          job={job} token={token}
          defaultQty={stop.pallets || stop.cartons || 1}
          submitFn={(payload) => driverPodStop(job.id, stop.sequence, payload)}
          onSaved={() => { setShowPod(false); onChanged?.(); }}
        />
      )}
      {podDone && (
        <div className="text-xs text-emerald-700 bg-emerald-50 rounded p-2 border border-emerald-200">
          Delivered to <span className="font-semibold">{stop.pod_recipient_name || '—'}</span>
          {stop.pod_captured_at && <> at {new Date(stop.pod_captured_at).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</>}
          {stop.pod_short_qty > 0 && <> · <span className="text-amber-700">{stop.pod_short_qty} short</span></>}
          {stop.pod_damaged_qty > 0 && <> · <span className="text-red-700">{stop.pod_damaged_qty} damaged</span></>}
          {stop.pod_photo_paths?.length > 0 && <> · {stop.pod_photo_paths.length} photo(s)</>}
        </div>
      )}
    </div>
  );
}

function MultiStopCard({ job, token, seq, onChanged }) {
  const stops = job.stops || [];
  const totalStops = stops.length;
  const doneStops = stops.filter((s) => s.pod_captured_at).length;
  const jobDone = job.status === 'delivered';

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3 space-y-3" data-testid={`driver-multistop-${job.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-white shrink-0 ${jobDone ? 'bg-emerald-600' : 'bg-indigo-600'}`}>
          {jobDone ? <CheckCircle size={18} weight="fill" /> : seq}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-mono text-slate-500">{job.job_number} · milk run</div>
          <div className="font-semibold text-sm">{job.customer_name || 'Multi-stop run'}</div>
          <div className="text-xs text-slate-600 flex items-start gap-1">
            <MapPin size={12} className="mt-0.5 shrink-0" />
            <span className="line-clamp-2">↑ {job.pickup_address || job.pickup_location || '—'}</span>
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {totalStops} stops · {job.pallets}p · {job.weight_kg}kg
            {job.dangerous_goods && <> · ⚠️ DG</>}
            {job.temperature && job.temperature !== 'ambient' && <> · ❄️ {job.temperature}</>}
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold text-indigo-600 tabular-nums" data-testid={`driver-multistop-progress-${job.id}`}>
            {doneStops}/{totalStops}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">done</div>
        </div>
      </div>
      {totalStops > 0 && (
        <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-indigo-500 transition-all"
               style={{ width: `${Math.round((doneStops / totalStops) * 100)}%` }} />
        </div>
      )}
      <div className="space-y-2">
        {stops.map((s) => (
          <MultiStopSubCard key={s.sequence} job={job} stop={s} parentSeq={totalStops}
                            token={token} onChanged={onChanged} />
        ))}
      </div>
      {jobDone && (
        <div className="text-xs text-emerald-700 bg-emerald-50 rounded p-2 border border-emerald-200 text-center font-semibold"
             data-testid={`driver-multistop-complete-${job.id}`}>
          All stops delivered — milk run complete ✓
        </div>
      )}
    </div>
  );
}

export default function DriverApp() {
  const token = useDriverToken();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = token ? { token } : {};
      const r = await getDriverRun(params);
      setRun(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load run');
      setRun(null);
    } finally {
      setLoading(false);
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  // iter216 — GPS pinger. Runs every 60s while there's at least one
  // in_transit job on the run so the customer tracking page shows a
  // fresh truck position. Silently no-ops if permission is denied.
  const [pingStatus, setPingStatus] = useState('idle');
  const [lastPingAt, setLastPingAt] = useState(null);
  useEffect(() => {
    if (!run) return undefined;
    const activeJobs = (run.jobs || []).filter((j) => j.status === 'in_transit');
    if (activeJobs.length === 0) { setPingStatus('idle'); return undefined; }
    if (!navigator.geolocation) { setPingStatus('unsupported'); return undefined; }
    let cancelled = false;
    const tick = () => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          if (cancelled) return;
          setPingStatus('active');
          const payload = {
            token: token || undefined,
            lat: pos.coords.latitude,
            lon: pos.coords.longitude,
            accuracy_m: pos.coords.accuracy,
            speed_mps: pos.coords.speed || undefined,
            heading_deg: pos.coords.heading || undefined,
          };
          try {
            // Ping every currently-active job so any customer link stays fresh.
            await Promise.all(activeJobs.map((j) => driverPing(j.id, payload)));
            setLastPingAt(new Date());
          } catch (_) { /* silent — next tick will retry */ }
        },
        () => { if (!cancelled) setPingStatus('denied'); },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
      );
    };
    tick();                                         // fire immediately
    const iv = setInterval(tick, 60000);            // then every 60s
    return () => { cancelled = true; clearInterval(iv); };
  }, [run, token]);

  if (!run) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 text-center">
        <div>
          <Truck size={40} className="text-slate-300 mx-auto mb-3" />
          <div className="text-sm text-slate-600">
            {loading ? 'Loading your run…' : token ? 'Invalid or expired driver link' : 'Please log in to view your run'}
          </div>
        </div>
      </div>
    );
  }

  const total = run.jobs.length;
  const done = run.jobs.filter((j) => j.status === 'delivered').length;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="driver-app">
      <div className="bg-slate-900 text-white p-3 sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <Truck size={20} weight="fill" className="text-brand" />
          <div>
            <div className="text-xs opacity-70">Driver run · {run.date}</div>
            <div className="text-sm font-semibold" data-testid="driver-header-name">
              {run.driver?.name}
              {run.vehicle && <span className="opacity-70"> · {run.vehicle.registration}</span>}
              {run.is_subcontractor && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30" data-testid="driver-subcontractor-badge">Subcontractor</span>}
            </div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-lg font-bold" data-testid="driver-progress">{done}/{total}</div>
            <div className="text-[10px] opacity-70">stops</div>
          </div>
        </div>
        {pingStatus !== 'idle' && (
          <div className="mt-1 text-[10px] flex items-center gap-1" data-testid="driver-gps-status">
            <span className={
              pingStatus === 'active' ? 'w-2 h-2 rounded-full bg-emerald-400 animate-pulse'
              : pingStatus === 'denied' ? 'w-2 h-2 rounded-full bg-red-400'
              : 'w-2 h-2 rounded-full bg-slate-400'
            } />
            <span className="opacity-70">
              {pingStatus === 'active' && (lastPingAt
                ? `GPS live · ${lastPingAt.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}`
                : 'GPS starting…')}
              {pingStatus === 'denied' && 'GPS permission denied — customers won\'t see live location'}
              {pingStatus === 'unsupported' && 'GPS unsupported on this device'}
            </span>
          </div>
        )}
        {total > 0 && (
          <div className="mt-2 h-1 bg-slate-700 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all"
                 style={{ width: `${Math.round((done / total) * 100)}%` }} />
          </div>
        )}
      </div>

      <div className="p-3 space-y-3 max-w-2xl mx-auto">
        {run.jobs.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-6 text-center text-sm text-slate-500">
            No stops assigned for {run.date}.
          </div>
        )}
        {run.jobs.map((j, i) => (
          j.multi_stop && Array.isArray(j.stops) && j.stops.length > 0
            ? <MultiStopCard key={j.id} job={j} token={token} seq={j.sequence_no || i + 1} onChanged={load} />
            : <StopCard key={j.id} job={j} token={token} seq={j.sequence_no || i + 1} onChanged={load} />
        ))}
        {total > 0 && done === total && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded p-4 text-center text-sm font-semibold" data-testid="driver-run-complete">
            <CheckCircle size={24} weight="fill" className="mx-auto mb-1" />
            Run complete — head back to the depot!
          </div>
        )}
      </div>
    </div>
  );
}
